import React, { useEffect, useState, useRef } from 'react';
import { useApp } from './AppContext';
import { useActiveCallStatus } from './useActiveCallStatus';

// High-fidelity, warm melodic chime synthesizer for incoming call alerts
export class CallChimeEngine {
    private ctx: AudioContext | null = null;
    private intervalId: any = null;
    private isRunning: boolean = false;
    private hasUserUnlocked: boolean = false;

    constructor() {
        if (typeof window !== 'undefined') {
            const unlockAudio = () => {
                if (!this.hasUserUnlocked) {
                    this.hasUserUnlocked = true;
                    this.getContext();
                }
            };
            window.addEventListener('pointerdown', unlockAudio, { once: true });
            window.addEventListener('keydown', unlockAudio, { once: true });
            window.addEventListener('touchstart', unlockAudio, { once: true });
        }
    }

    private getContext(): AudioContext | null {
        try {
            if (!this.ctx || this.ctx.state === 'closed') {
                const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
                if (AudioCtx) {
                    this.ctx = new AudioCtx();
                }
            }
            if (this.ctx && this.ctx.state === 'suspended') {
                this.ctx.resume().catch(() => {});
            }
            return this.ctx;
        } catch (e) {
            return null;
        }
    }

    public playSingleChime() {
        const ctx = this.getContext();
        if (!ctx) return;

        try {
            const now = ctx.currentTime;
            
            // Soft marimba-like harmonic frequencies: D5 (587.33Hz), F#5 (739.99Hz), A5 (880Hz), D6 (1174.66Hz)
            const notes = [
                { freq: 587.33, start: 0, duration: 0.7, gain: 0.16 },
                { freq: 739.99, start: 0.12, duration: 0.7, gain: 0.15 },
                { freq: 880.00, start: 0.24, duration: 0.8, gain: 0.17 },
                { freq: 1174.66, start: 0.36, duration: 1.0, gain: 0.14 }
            ];

            notes.forEach(note => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();

                osc.type = 'sine';
                osc.frequency.setValueAtTime(note.freq, now + note.start);

                gain.gain.setValueAtTime(0.0001, now + note.start);
                gain.gain.exponentialRampToValueAtTime(note.gain, now + note.start + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + note.start + note.duration);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(now + note.start);
                osc.stop(now + note.start + note.duration + 0.05);
            });
        } catch (e) {
            // Browser autoplay policy catch
        }
    }

    public startContinuousRing() {
        if (this.isRunning) return;
        this.isRunning = true;
        
        // Play immediately
        this.playSingleChime();

        // Repeat continuously every 2.4 seconds until stopped
        this.intervalId = setInterval(() => {
            if (this.isRunning) {
                this.playSingleChime();
            }
        }, 2400);
    }

    public stopContinuousRing() {
        this.isRunning = false;
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    public stopAll() {
        this.stopContinuousRing();
    }
}

export const globalChimeEngine = new CallChimeEngine();

// --- IN-PAGE INCOMING CALL BANNER (HUMANE & GENTLE) ---
export const LiveCallNotificationBanner: React.FC<{ onJoinRoom: (id: string) => void }> = ({ onJoinRoom }) => {
    const { appointments, user, allUsers, doctors } = useApp();
    const { isBothConnectedInCall, hasUserJoinedCall, isCallEnded, callDocStatus } = useActiveCallStatus();
    const [isDismissed, setIsDismissed] = useState(false);

    // Look for in-progress appointment for this user
    const liveSession = appointments.find(app => 
        app.status === 'in-progress' && 
        (app.patientId === user?.id || app.doctorId === user?.id)
    );

    // Track session activity to re-open notification when counterpart joins or rejoins
    const sessionKey = `${liveSession?.id || ''}_${callDocStatus[liveSession?.id || '']?.doctorJoinedAt || 0}_${callDocStatus[liveSession?.id || '']?.patientJoinedAt || 0}_${callDocStatus[liveSession?.id || '']?.currentSessionId || ''}`;
    const lastSessionKeyRef = React.useRef<string>('');

    React.useEffect(() => {
        if (sessionKey !== lastSessionKeyRef.current) {
            lastSessionKeyRef.current = sessionKey;
            setIsDismissed(false);
        }
    }, [sessionKey]);

    if (!liveSession || isDismissed) return null;

    // 1. Close immediately if the call has ended by either doctor or patient
    if (isCallEnded(liveSession.id)) {
        return null;
    }

    // 2. Close immediately when both of them are in the call with each other
    if (isBothConnectedInCall(liveSession.id)) {
        return null;
    }

    // 3. Do not show if another page is opened after each one has joined the call
    if (hasUserJoinedCall(liveSession.id)) {
        return null;
    }

    const counterpartId = user?.role === 'doctor' ? liveSession.patientId : liveSession.doctorId;
    const counterpartUser = allUsers.find(u => u.id === counterpartId) || doctors.find(d => d.id === counterpartId);
    
    let counterpartDisplayName = '';
    let warmSubtitle = '';

    if (user?.role === 'doctor') {
        const patientName = counterpartUser?.name || liveSession.patientName || 'your patient';
        counterpartDisplayName = patientName;
        warmSubtitle = `${patientName} is in the consultation room and waiting for you to join.`;
    } else {
        const rawDoctorName = counterpartUser?.name || liveSession.doctorName || 'your doctor';
        const formattedDoctorName = rawDoctorName.toLowerCase().startsWith('dr') ? rawDoctorName : `Dr. ${rawDoctorName}`;
        counterpartDisplayName = formattedDoctorName;
        warmSubtitle = `${formattedDoctorName} has opened your consultation room and is waiting to see you.`;
    }

    return (
        <div 
            role="alert"
            aria-label="Your video visit is ready"
            className="w-full bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 text-white p-5 md:p-6 rounded-3xl shadow-xl mb-8 flex flex-col sm:flex-row items-center justify-between border border-emerald-400/30 animate-in fade-in duration-500 font-['Plus_Jakarta_Sans'] relative"
        >
            <div className="flex items-center gap-4 mb-4 sm:mb-0 pr-8 sm:pr-0">
                <div className="w-13 h-13 bg-white/20 rounded-2xl flex items-center justify-center text-2xl shrink-0 shadow-inner" aria-hidden="true">
                    <i className="fa-solid fa-video animate-pulse"></i>
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-300 animate-ping"></span>
                        <h4 className="font-black text-base md:text-lg leading-tight tracking-tight">Your Video Visit is Ready</h4>
                    </div>
                    <p className="text-xs md:text-sm text-emerald-100 font-medium opacity-95 mt-1">
                        {warmSubtitle}
                    </p>
                </div>
            </div>
            
            <div className="flex items-center gap-3 w-full sm:w-auto">
                <button
                    onClick={() => onJoinRoom(liveSession.id)}
                    aria-label={`Join video consultation with ${counterpartDisplayName}`}
                    className="flex-1 sm:flex-none bg-white text-teal-800 hover:bg-emerald-50 transition-all font-black px-7 py-3.5 rounded-2xl text-xs tracking-wider uppercase shadow-lg shadow-emerald-950/20 active:scale-95 outline-none cursor-pointer flex items-center justify-center gap-2.5 shrink-0"
                >
                    <i className="fa-solid fa-phone text-emerald-600 text-sm"></i> 
                    <span>Join Consultation</span>
                </button>
                <button
                    onClick={() => setIsDismissed(true)}
                    className="p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-2xl transition-colors cursor-pointer shrink-0"
                    title="Dismiss alert"
                    aria-label="Dismiss notification"
                >
                    <i className="fa-solid fa-xmark text-sm"></i>
                </button>
            </div>
        </div>
    );
};

// --- GLOBAL FLOATING CALL POPUP WITH CONTINUOUS CHIMES & PUSH NOTIFICATIONS ---
export const GlobalLiveCallPopup: React.FC<{ onJoinRoom: (id: string) => void }> = ({ onJoinRoom }) => {
    const { appointments, user, allUsers, doctors } = useApp();
    const { isBothConnectedInCall, hasUserJoinedCall, isCallEnded, callDocStatus } = useActiveCallStatus();
    const [dismissedSessionId, setDismissedSessionId] = useState<string | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [notificationPermission, setNotificationPermission] = useState<string>(() => {
        return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
    });

    const liveSession = appointments.find(app => 
        app.status === 'in-progress' && 
        (app.patientId === user?.id || app.doctorId === user?.id)
    );

    // Re-enable popup notification when a new call session is initiated or participant rejoins
    const sessionKey = `${liveSession?.id || ''}_${callDocStatus[liveSession?.id || '']?.doctorJoinedAt || 0}_${callDocStatus[liveSession?.id || '']?.patientJoinedAt || 0}_${callDocStatus[liveSession?.id || '']?.currentSessionId || ''}`;
    const lastSessionKeyRef = React.useRef<string>('');

    React.useEffect(() => {
        if (sessionKey !== lastSessionKeyRef.current) {
            lastSessionKeyRef.current = sessionKey;
            setDismissedSessionId(null);
        }
    }, [sessionKey]);

    const callShouldClose = !liveSession || 
        liveSession.id === dismissedSessionId || 
        isCallEnded(liveSession.id) || 
        isBothConnectedInCall(liveSession.id) || 
        hasUserJoinedCall(liveSession.id);

    // Auto-request or check push notification status
    const requestPushNotificationPermission = async () => {
        if (typeof window !== 'undefined' && 'Notification' in window) {
            try {
                const res = await Notification.requestPermission();
                setNotificationPermission(res);
                if (res === 'granted') {
                    try {
                        new Notification("ROVITA Virtual Care", {
                            body: "Notifications are enabled. We will alert you whenever your doctor is ready.",
                            icon: '/favicon.png'
                        });
                    } catch (e) {}
                }
            } catch (e) {}
        }
    };

    // Continuous Chime Loop + Vibration + Push Notification
    useEffect(() => {
        if (callShouldClose) {
            globalChimeEngine.stopContinuousRing();
            return;
        }

        // 1. Start continuous audio chime unless muted
        if (!isMuted) {
            globalChimeEngine.startContinuousRing();
        } else {
            globalChimeEngine.stopContinuousRing();
        }

        // 2. Vibrate mobile device gently
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
            try {
                navigator.vibrate([200, 100, 200, 100, 400]);
            } catch (e) {}
        }

        // 3. Trigger Native Push / Web Notification
        const callerName = user?.role === 'doctor' 
            ? (liveSession.patientName || 'Your patient') 
            : (liveSession.doctorName?.startsWith('Dr') ? liveSession.doctorName : `Dr. ${liveSession.doctorName || 'Doctor'}`);

        if (typeof window !== 'undefined' && 'Notification' in window) {
            if (Notification.permission === 'granted') {
                const notifTitle = `ROVITA — ${callerName} is in the room`;
                const notifOptions = {
                    body: "Your consultation is ready to begin. Tap here to join now.",
                    icon: '/favicon.png',
                    badge: '/favicon.png',
                    tag: `call_${liveSession.id}`,
                    requireInteraction: true,
                    data: { url: `/#dashboard?activeSession=${liveSession.id}`, sessionId: liveSession.id }
                };

                // PWA / Service Worker notification delivery
                if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
                    navigator.serviceWorker.ready.then(reg => {
                        reg.showNotification(notifTitle, notifOptions).catch(() => {
                            try {
                                const notif = new Notification(notifTitle, notifOptions);
                                notif.onclick = () => {
                                    window.focus();
                                    globalChimeEngine.stopContinuousRing();
                                    onJoinRoom(liveSession.id);
                                };
                            } catch (e) {}
                        });
                    });
                } else {
                    try {
                        const notif = new Notification(notifTitle, notifOptions);
                        notif.onclick = () => {
                            window.focus();
                            globalChimeEngine.stopContinuousRing();
                            onJoinRoom(liveSession.id);
                        };
                    } catch (e) {}
                }
            } else if (Notification.permission === 'default') {
                // Request permission automatically so user stays notified
                requestPushNotificationPermission();
            }
        }

        return () => {
            globalChimeEngine.stopContinuousRing();
        };
    }, [callShouldClose, isMuted, user?.role, liveSession]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            globalChimeEngine.stopContinuousRing();
        };
    }, []);

    if (callShouldClose) return null;

    const counterpartId = user?.role === 'doctor' ? liveSession.patientId : liveSession.doctorId;
    const counterpartUser = allUsers.find(u => u.id === counterpartId) || doctors.find(d => d.id === counterpartId);
    
    let counterpartDisplayName = '';
    let warmStatusText = '';

    if (user?.role === 'doctor') {
        const patientName = counterpartUser?.name || liveSession.patientName || 'Patient';
        counterpartDisplayName = patientName;
        warmStatusText = 'is waiting in the consultation room';
    } else {
        const rawDoctorName = counterpartUser?.name || liveSession.doctorName || 'Doctor';
        const formattedDoctorName = rawDoctorName.toLowerCase().startsWith('dr') ? rawDoctorName : `Dr. ${rawDoctorName}`;
        counterpartDisplayName = formattedDoctorName;
        warmStatusText = 'is ready to see you now';
    }

    const handleJoinCall = () => {
        globalChimeEngine.stopContinuousRing();
        onJoinRoom(liveSession.id);
    };

    const handleDismiss = () => {
        globalChimeEngine.stopContinuousRing();
        setDismissedSessionId(liveSession.id);
    };

    return (
        <div 
            className="fixed top-5 left-4 right-4 sm:left-auto sm:right-6 sm:w-[410px] z-[9999] animate-in slide-in-from-top-6 duration-300 pointer-events-auto font-['Plus_Jakarta_Sans']"
            role="alert"
            aria-live="assertive"
        >
            <div className="bg-slate-900/95 backdrop-blur-2xl text-white rounded-3xl p-5 sm:p-6 shadow-2xl border-2 border-emerald-500/60 shadow-emerald-500/20">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3.5">
                        <div className="relative">
                            <div className="w-13 h-13 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 flex items-center justify-center text-white text-xl shadow-lg">
                                <i className="fa-solid fa-video animate-pulse"></i>
                            </div>
                            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-rose-500 rounded-full border-2 border-slate-900 animate-ping"></span>
                            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-rose-500 rounded-full border-2 border-slate-900"></span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-300 bg-emerald-950/90 px-2 py-0.5 rounded-md border border-emerald-500/30 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                                    Consultation Ready
                                </span>
                            </div>
                            <h4 className="font-black text-base text-white mt-1 leading-snug">{counterpartDisplayName}</h4>
                            <p className="text-xs font-medium text-slate-300">{warmStatusText}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button 
                            onClick={() => setIsMuted(!isMuted)}
                            className={`p-2 rounded-xl text-xs transition-colors ${isMuted ? 'text-amber-400 bg-amber-400/10' : 'text-slate-400 hover:text-white bg-slate-800/60'}`}
                            title={isMuted ? "Unmute ring chime" : "Mute ring chime"}
                            aria-label={isMuted ? "Unmute incoming call chime" : "Mute incoming call chime"}
                        >
                            <i className={`fa-solid ${isMuted ? 'fa-volume-xmark' : 'fa-volume-high'}`}></i>
                        </button>
                        <button 
                            onClick={handleDismiss}
                            className="text-slate-400 hover:text-white p-2 rounded-xl bg-slate-800/60 hover:bg-slate-800 transition-colors"
                            title="Dismiss notification"
                            aria-label="Dismiss call alert"
                        >
                            <i className="fa-solid fa-xmark text-sm"></i>
                        </button>
                    </div>
                </div>

                {/* Push Notification prompt if not yet allowed */}
                {notificationPermission === 'default' && (
                    <div className="mb-4 p-2.5 bg-emerald-950/60 border border-emerald-500/30 rounded-2xl flex items-center justify-between gap-2">
                        <p className="text-[11px] text-emerald-200 font-medium">
                            <i className="fa-regular fa-bell text-emerald-400 mr-1.5"></i>
                            Enable visit alerts on your device
                        </p>
                        <button
                            onClick={requestPushNotificationPermission}
                            className="px-2.5 py-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-[10px] font-black rounded-lg transition-all uppercase tracking-wider"
                        >
                            Enable
                        </button>
                    </div>
                )}

                <div className="flex gap-2.5">
                    <button
                        onClick={handleJoinCall}
                        className="flex-1 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-black uppercase tracking-wider rounded-2xl shadow-lg shadow-emerald-600/30 transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                    >
                        <i className="fa-solid fa-phone"></i>
                        <span>Join Consultation</span>
                    </button>
                    <button
                        onClick={handleDismiss}
                        className="px-4 py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-2xl transition-all"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
};

